import { api } from './http';
import type {
  AttorneyReviewSurveyResponse,
  Matter,
  MatterAttorney,
  MatterCoClient,
  MatterListResponse,
} from './types';

export const getMatters = (params?: {
  pipeline_id?: number;
  stage_id?: number;
  contact_id?: number;
  attorney_id?: number;
  paralegal_id?: number;
  intake_paralegal_id?: number;
  assigned_staff_id?: number;
  practice_area_id?: number;
  co_counsel?: string;
  client_county?: string;
  priority_label?: string;
  intake_origin?: string;
  tag_id?: number;
  tag_ids?: string;
  sub_status?: string;
  state?: string;
  assigned_to_me?: boolean;
  status?: string;
  archive_filter?: 'active' | 'archived' | 'all';
  date_field?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  sort_by?: string;
  sort_dir?: string;
  lead_status_filter?: 'exclude_pending' | 'only_pending' | 'all';
  limit?: number;
  offset?: number;
}) => api.get<MatterListResponse>('/matters', { params }).then(r => r.data);

export const getMatterFilterStates = (params?: {
  pipeline_id?: number;
  archive_filter?: 'active' | 'archived' | 'all';
  lead_status_filter?: 'exclude_pending' | 'only_pending' | 'all';
}) => api.get<string[]>('/matters/filter-states', { params }).then(r => r.data);

export interface UnreachableReviewMatter {
  matter_id: number;
  title: string;
  case_number: string | null;
  client_name: string | null;
  assigned_paralegal: string | null;
  sub_status: string | null;
  parked_at: string | null;
  recent_call_count: number;
  last_contact_at: string | null;
}

/** Supervisor view: matters parked in an unreachable-review sub-status (admin/supervisor only). */
export const getUnreachableReview = () =>
  api.get<UnreachableReviewMatter[]>('/matters/unreachable-review').then(r => r.data);

export const getCoCounselValues = () =>
  api.get<string[]>('/matters/co-counsel-values').then(r => r.data);

export const getMatter = (id: number) =>
  api.get<Matter>(`/matters/${id}`).then(r => r.data);

export const createMatter = (data: Partial<Matter>) =>
  api.post<Matter>('/matters', data).then(r => r.data);

export const updateMatter = (id: number, data: Partial<Matter>) =>
  api.patch<Matter>(`/matters/${id}`, data).then(r => r.data);

export const previewFolderName = (data: { title: string; client_first?: string; client_last?: string }) =>
  api.post<{ original: string; abbreviated: string; length: number; max_chars: number; needs_abbreviation: boolean }>('/matters/preview-folder-name', data).then(r => r.data);

export const archiveMatter = (id: number) =>
  api.post<Matter>(`/matters/${id}/archive`).then(r => r.data);

export const unarchiveMatter = (id: number) =>
  api.post<Matter>(`/matters/${id}/unarchive`).then(r => r.data);

export const bulkUpdateMatters = (body: { matter_ids: number[]; updates: Record<string, unknown>; preview?: boolean }) =>
  api.post('/matters/bulk-update', body).then(r => r.data);

export const submitAttorneyDecision = (matterId: number, data: { decision: 'approve' | 'reject'; notes?: string }) =>
  api.post<Matter>(`/matters/${matterId}/attorney-decision`, data).then(r => r.data);

export const submitAttorneyReviewSurvey = (data: {
  matter_id: number;
  sufficiently_papered: boolean;
  meets_criteria: boolean;
  rejection_reason?: string;
}) =>
  api.post<AttorneyReviewSurveyResponse>('/attorney-review-surveys', data).then(r => r.data);

export const getLatestAttorneyReviewSurvey = (matterId: number) =>
  api.get<AttorneyReviewSurveyResponse | null>(`/attorney-review-surveys/matter/${matterId}/latest`).then(r => r.data);

export const getMatterAttorneys = (matterId: number) =>
  api.get<MatterAttorney[]>(`/matters/${matterId}/attorneys`).then(r => r.data);

export const addMatterAttorney = (matterId: number, data: { user_id: number; role_label?: string }) =>
  api.post<MatterAttorney>(`/matters/${matterId}/attorneys`, data).then(r => r.data);

export const removeMatterAttorney = (matterId: number, recordId: number) =>
  api.delete(`/matters/${matterId}/attorneys/${recordId}`).then(r => r.data);

export const getMatterCoClients = (matterId: number) =>
  api.get<MatterCoClient[]>(`/matters/${matterId}/co-clients`).then(r => r.data);

export const addMatterCoClient = (matterId: number, data: { contact_id: number; label?: string }) =>
  api.post<MatterCoClient>(`/matters/${matterId}/co-clients`, data).then(r => r.data);

export const removeMatterCoClient = (matterId: number, coClientId: number) =>
  api.delete(`/matters/${matterId}/co-clients/${coClientId}`).then(r => r.data);
